// Universal Switch Scanning Engine for AbilityOS
// Supports 1-Switch Autoscan, 2-Switch Step-Scan, and Audio Feedback

export type SwitchScanMode = 'off' | '1-switch-auto' | '2-switch-step';

export interface SwitchScanOptions {
  mode: SwitchScanMode;
  speedMs: number; // Interval for autoscan (e.g., 1500ms)
  audioCue: boolean;
  onScanChange?: (index: number, total: number) => void;
}

class SwitchScanningEngine {
  private mode: SwitchScanMode = 'off';
  private speedMs: number = 1500;
  private audioCue: boolean = true;
  private currentIndex: number = -1;
  private scanTimer: any = null;
  private elements: HTMLElement[] = [];
  private activeHighlightClass = 'abilityos-switch-active-target';

  constructor() {
    this.initStyle();
    this.bindEvents();
  }

  private initStyle() {
    if (typeof document === 'undefined') return;
    if (document.getElementById('abilityos-switch-style')) return;

    const style = document.createElement('style');
    style.id = 'abilityos-switch-style';
    style.textContent = `
      .abilityos-switch-target {
        transition: transform 0.15s ease, box-shadow 0.15s ease, outline 0.15s ease !important;
      }
      .abilityos-switch-active-target {
        outline: 4px solid #ffd64e !important;
        outline-offset: 3px !important;
        box-shadow: 0 0 24px rgba(255, 214, 78, 0.9) !important;
        transform: scale(1.06) !important;
        z-index: 9999 !important;
      }
      .high-contrast .abilityos-switch-active-target {
        outline: 5px solid #ffff00 !important;
        box-shadow: 0 0 30px #ffff00 !important;
      }
    `;
    document.head.appendChild(style);
  }

  public setConfig(options: Partial<SwitchScanOptions>) {
    if (options.mode !== undefined) {
      this.mode = options.mode;
    }
    if (options.speedMs !== undefined) {
      this.speedMs = options.speedMs;
    }
    if (options.audioCue !== undefined) {
      this.audioCue = options.audioCue;
    }

    if (this.mode === 'off') {
      this.stop();
    } else {
      this.start();
    }
  }

  public refreshTargets() {
    if (typeof document === 'undefined') return;
    const raw = Array.from(
      document.querySelectorAll<HTMLElement>(
        '[data-switch-item], button.switch-target, [role="button"].switch-target, .aac-tile'
      )
    ).filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).display !== 'none';
    });

    this.elements = raw;
    this.elements.forEach(el => el.classList.add('abilityos-switch-target'));

    if (this.currentIndex >= this.elements.length) {
      this.currentIndex = 0;
    }
  }

  public start() {
    this.stop();
    this.refreshTargets();
    if (this.elements.length === 0) return;

    this.currentIndex = 0;
    this.highlightCurrent();

    if (this.mode === '1-switch-auto') {
      this.scanTimer = setInterval(() => {
        this.stepForward();
      }, this.speedMs);
    }
  }

  public stop() {
    if (this.scanTimer) {
      clearInterval(this.scanTimer);
      this.scanTimer = null;
    }
    this.clearHighlight();
    this.currentIndex = -1;
  }

  public stepForward() {
    this.refreshTargets();
    if (this.elements.length === 0) return;

    this.clearHighlight();
    this.currentIndex = (this.currentIndex + 1) % this.elements.length;
    this.highlightCurrent();
  }

  public stepBackward() {
    this.refreshTargets();
    if (this.elements.length === 0) return;

    this.clearHighlight();
    this.currentIndex = (this.currentIndex - 1 + this.elements.length) % this.elements.length;
    this.highlightCurrent();
  }

  public activateCurrent() {
    if (this.currentIndex < 0 || this.currentIndex >= this.elements.length) return;
    const target = this.elements[this.currentIndex];
    if (target) {
      target.click();
      this.playChime(700);

      target.classList.add('abilityos-switch-selected');
      setTimeout(() => target.classList.remove('abilityos-switch-selected'), 300);
    }
  }

  private highlightCurrent() {
    if (this.currentIndex < 0 || this.currentIndex >= this.elements.length) return;
    const target = this.elements[this.currentIndex];
    if (!target) return;

    target.classList.add(this.activeHighlightClass);
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    if (this.audioCue) {
      const label = target.getAttribute('aria-label') || target.innerText || 'Item';
      this.playAudioCue(label.slice(0, 30));
    }
  }

  private clearHighlight() {
    if (this.currentIndex >= 0 && this.currentIndex < this.elements.length) {
      this.elements[this.currentIndex]?.classList.remove(this.activeHighlightClass);
    }
  }

  private playAudioCue(text: string) {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.3;
      u.volume = 0.5;
      window.speechSynthesis.speak(u);
    }
  }

  private playChime(freq = 600) {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {
      // AudioContext safe
    }
  }

  private bindEvents() {
    if (typeof window === 'undefined') return;

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.mode === 'off') return;

      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (this.mode === '1-switch-auto') {
          this.activateCurrent();
        } else if (this.mode === '2-switch-step') {
          this.stepForward();
        }
      }

      if (e.code === 'Enter') {
        if (this.mode === '2-switch-step') {
          e.preventDefault();
          this.activateCurrent();
        }
      }
    });
  }

  public getMode(): SwitchScanMode {
    return this.mode;
  }
}

export const switchScanner = new SwitchScanningEngine();
